import React from 'react';

import useMediaQuery from '@material-ui/core/useMediaQuery';
import { default as EditIcon } from '@material-ui/icons/Edit';

import { useCreatorLevel } from './Connectable.hooks';

import { DESKTOP } from '@/app/constants/dictionaries/ScreenSizeConst';

import { IReportEntity, sourceSetIds, TSystemObject } from '@/app/state/types';
import {
    activatePreview,
    fetchLastStates,
} from '../../../../../../../../state/_actions';
import {
    activateDetectionSettingsPane,
    TComponentType,
} from '@/app/state/ui/discovery/snapshotting';
import { usePrivileges } from '@/app/state/auth/index.hooks';
import { showEditMonitoredObjectDialog } from '@/app/state/ui/dialog';

import TranslationHelper from '../../../../../../../../helpers/TranslationHelper';
import { useAppDispatch } from '@/app/hooks';

import MenuButtons from '../MenuButtons';
import PaneHeader from '../../../../../../../../components/PaneHeader';
import GoogleMapsButton from '../../../../../../../../components/common/GoogleMapsButton';
import MenuDropdown from '../../../../../../../../components/MenuDropdown';
import IconButtonWithTooltip from '../../../../../../../../components/IconButtonWithTooltip';
import { useLastState } from '../../../../../../selectors/composed/index.hooks';
import { useGridFilters } from '@/app/pages/discovery/selectors/index.hooks';
import { RouteTankEditPage, RouteVehicleEditPage } from '@/app/pages/paths';
import { useHistory } from 'react-router-dom';
import { objectTypes } from '@/app/components/globalDialog/components/EditObjectDialog/utils/fields';

interface IOwnProps {
    handleCloseClick: () => void;
    getMenuAction: (name: string) => IReportEntity | undefined;
}

const LastStatePreviewHeader = ({
    handleCloseClick,
    getMenuAction,
}: IOwnProps) => {
    const dispatch = useAppDispatch();
    const history = useHistory();

    const lastState = useLastState();
    const userPrivileges = usePrivileges();
    const creatorLevel = useCreatorLevel();
    const gridFilters = useGridFilters();

    const mdUpSize = useMediaQuery(DESKTOP);

    const coordinatesAllowed = getMenuAction('coordinates');
    const recalculatingAllowed = getMenuAction('recalculate');
    const registerRefuelingAllowed = getMenuAction('registerRefueling');
    const fuelDistributionToggle = getMenuAction('fuelDistributionToggle');
    const tunnelToggle = getMenuAction('tunnelToggle');
    const refuelingAndFuelLossDetectionSettings = getMenuAction(
        'refuelingAndFuelLossDetectionSettings'
    );
    const openDiagnosticsForDevice = getMenuAction('openDiagnosticsForDevice');
    const editObject = getMenuAction('edit');

    const menuActions = {
        coordinatesAllowed,
        recalculatingAllowed,
        registerRefuelingAllowed,
        refuelingAndFuelLossDetectionSettings,
        fuelDistributionToggle,
        tunnelToggle,
        openDiagnosticsForDevice,
    };

    const lastStateMetaHeader = lastState?._meta?.header;

    if (!lastStateMetaHeader) return <></>;

    const lastStateName = lastStateMetaHeader?.name;

    const editObjectAccess = editObject && userPrivileges.registersContext;
    const canEditObject =
        editObjectAccess &&
        lastStateMetaHeader.externalId &&
        lastStateMetaHeader.type;

    const handleOpenDetectorSettings = () => {
        const refuelingAndFuelLossDetectionSettings = getMenuAction(
            'refuelingAndFuelLossDetectionSettings'
        );
        const action = refuelingAndFuelLossDetectionSettings?.actions?.onClick;
        if (action?.componentName) {
            dispatch(
                activateDetectionSettingsPane(
                    {
                        type:
                            (action.componentName as TComponentType) ||
                            'source-set-chart',
                        level: creatorLevel + 1,
                    },
                    lastState.id,
                    action.componentName,
                    {
                        api: '/rest/api/fueling/detection/config',
                        label: 'GET',
                        method: 'GET',
                        params: {
                            monitoredId: Number(lastState.id),
                        },
                    },
                    false,
                    false
                )
            );
        }
    };

    const handleOpenSetCoordinates = () => {
        dispatch(
            activatePreview(
                sourceSetIds.lastStates,
                lastState.id,
                'monitoredObject',
                'add',
                { type: 'preview', level: creatorLevel + 1 },
                undefined,
                false,
                false
            )
        );
    };

    const handleEditObject = (
        objectId: number,
        externalId: number,
        type: TSystemObject,
        onSuccess: () => void
    ) => {
        if (userPrivileges.newTankEditionPage && type === objectTypes.TANK) {
            history.push(
                RouteTankEditPage.replace(':id', externalId.toString()) +
                    `?returnUrl=${encodeURIComponent(
                        history.location.pathname + history.location.search
                    )}`
            );
        } else if (type === objectTypes.VEHICLE) {
            history.push(
                RouteVehicleEditPage.replace(':id', externalId.toString()) +
                    `?returnUrl=${encodeURIComponent(
                        history.location.pathname + history.location.search
                    )}`
            );
        } else {
            dispatch(
                showEditMonitoredObjectDialog({
                    headerData: { objectId, type },
                    onSuccess,
                })
            );
        }
    };

    const handleOpenRegisterEvent = () => {
        const registerRefuelingAllowed = getMenuAction('registerRefueling');
        const action =
            registerRefuelingAllowed?.actions?.onClick?.componentName;
        if (action) {
            dispatch(
                activatePreview(
                    sourceSetIds.lastStates,
                    lastState.id,
                    action,
                    'add',
                    {
                        type: (action as TComponentType) || 'preview',
                        level: creatorLevel + 1,
                    },
                    {
                        api: '/rest/api/monitored/archives/{monitoredId}/user-events',
                        label: 'POST',
                        method: 'POST',
                        params: {
                            monitoredId: Number(lastState.id),
                        },
                    },
                    false,
                    false
                )
            );
        }
    };

    const showMenuButtons = () => {
        const menuButtonsAsBooleans = Object.values(menuActions).map(
            (action) => {
                if (Array.isArray(action)) {
                    return !!action.length;
                }
                return !!action;
            }
        );
        return menuButtonsAsBooleans.includes(true);
    };

    return (
        <PaneHeader
            title={lastStateName}
            onCloseClick={handleCloseClick}
            renderRightCustomControls={() => (
                <>
                    {mdUpSize && showMenuButtons() && (
                        <MenuDropdown>
                            <MenuButtons
                                menuActions={menuActions}
                                handleOpenDetectorSettings={
                                    handleOpenDetectorSettings
                                }
                                handleOpenRegisterEvent={
                                    handleOpenRegisterEvent
                                }
                                handleOpenSetCoordinates={
                                    handleOpenSetCoordinates
                                }
                            />
                        </MenuDropdown>
                    )}
                    {canEditObject && (
                        <IconButtonWithTooltip
                            onClick={() =>
                                handleEditObject(
                                    lastStateMetaHeader.monitoredId,
                                    lastStateMetaHeader.externalId,
                                    lastStateMetaHeader.type,
                                    () =>
                                        dispatch(
                                            fetchLastStates({
                                                ...gridFilters,
                                                differential: true,
                                            })
                                        )
                                )
                            }
                            title={TranslationHelper.translate(
                                editObject?.label || ''
                            )}
                        >
                            <EditIcon />
                        </IconButtonWithTooltip>
                    )}
                    <GoogleMapsButton
                        coordinates={lastStateMetaHeader?.coordinates}
                    />
                </>
            )}
        />
    );
};

export default LastStatePreviewHeader;
